import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Eye, Users, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';

interface ActiveVisit {
  id: string;
  customer_id: string | null;
  entered_at: string;
  camera_id?: string | null;
  customer?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

interface Customer {
  id: string;
  name: string;
  avatar?: string;
  entryTime: string;
  status: 'browsing' | 'assisted' | 'checkout';
  visitId: string;
}

const statusConfig = {
  browsing: { label: 'Geziniyor', color: 'bg-info/20 text-info' },
  assisted: { label: 'Yardım Alıyor', color: 'bg-warning/20 text-warning' },
  checkout: { label: 'Ödeme', color: 'bg-success/20 text-success' },
};

export const WhosInside = () => {
  const { companyId, loading: authLoading } = useAuth();

  const { data: activeVisits = [], isLoading } = useQuery({
    queryKey: ['activeVisits', companyId],
    queryFn: async (): Promise<ActiveVisit[]> => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from('visits')
        .select(`
          id,
          customer_id,
          entered_at,
          camera_id,
          customer:customers (
            id,
            first_name,
            last_name
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'PENDING_LABEL')
        .order('entered_at', { ascending: false })
        .limit(10);
      
      if (error) {
        console.error('Error fetching active visits:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!companyId && !authLoading,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCustomerName = (visit: ActiveVisit) => {
    if (visit.customer?.first_name || visit.customer?.last_name) {
      return `${visit.customer.first_name || ''} ${visit.customer.last_name || ''}`.trim();
    }
    return 'Bilinmeyen Müşteri';
  };

  const getVisitStatus = (visit: ActiveVisit): 'browsing' | 'assisted' | 'checkout' => {
    // For now, we'll determine status based on visit duration
    const entryTime = new Date(visit.entered_at);
    const now = new Date();
    const durationMinutes = (now.getTime() - entryTime.getTime()) / (1000 * 60);
    
    if (durationMinutes < 5) return 'browsing';
    if (durationMinutes < 20) return 'assisted';
    return 'checkout';
  };

  const customers: Customer[] = activeVisits.map((visit) => ({
    id: visit.customer_id || visit.id,
    visitId: visit.id,
    name: getCustomerName(visit),
    entryTime: formatTime(visit.entered_at),
    status: getVisitStatus(visit),
  }));

  if (authLoading || isLoading) {
    return (
      <div className="glass-panel p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <div>
              <Skeleton className="w-32 h-6 mb-2" />
              <Skeleton className="w-24 h-4" />
            </div>
          </div>
          <Skeleton className="w-20 h-8 rounded-lg" />
        </div>
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex items-center gap-4 p-4 rounded-xl bg-white/5">
              <Skeleton className="w-12 h-12 rounded-full" />
              <div className="flex-1">
                <Skeleton className="w-24 h-5 mb-2" />
                <Skeleton className="w-16 h-4" />
              </div>
              <Skeleton className="w-16 h-6 rounded-full" />
              <Skeleton className="w-20 h-8 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="glass-panel p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
            <Eye className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h2 className="text-h3">İçeride Kimler Var</h2>
            <p className="text-caption text-muted-foreground">Anlık müşteri takibi</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/10">
          <Users className="w-4 h-4 text-accent" />
          <span className="font-semibold text-accent">{customers.length}</span>
          <span className="text-caption">müşteri</span>
        </div>
      </div>

      <div className="space-y-3">
        {customers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Şu anda mağazada aktif müşteri bulunmuyor</p>
          </div>
        ) : (
          customers.map((customer) => (
            <div
              key={customer.visitId}
              className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/8 transition-all duration-200 hover-lift"
            >
              <div className="relative">
                <Avatar className="w-12 h-12 border-2 border-white/20">
                  <AvatarImage src={customer.avatar} alt={customer.name} />
                  <AvatarFallback className="bg-accent/20 text-accent font-semibold">
                    {customer.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-background pulse-slow"></div>
              </div>

              <div className="flex-1">
                <h3 className="font-semibold">{customer.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Clock className="w-3 h-3 text-muted-foreground" />
                  <span className="text-caption text-muted-foreground">{customer.entryTime}</span>
                </div>
              </div>

              <div className={cn(
                'px-3 py-1 rounded-full text-xs font-medium',
                statusConfig[customer.status].color
              )}>
                {statusConfig[customer.status].label}
              </div>

              <Button variant="ghost" size="sm">
                Görüntüle
              </Button>
            </div>
          ))
        )}
      </div>

      <Button className="w-full" variant="outline" size="lg">
        <Users className="w-4 h-4 mr-2" />
        Tüm Ziyaretçileri Görüntüle
      </Button>
    </div>
  );
};